import { useNavigate } from 'react-router-dom';

import { ICON_SIZES } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useGetCollegeByNameQuery } from '@backrs_ui/grow-ui/src/redux/college/college.api';
import {
  ProfileValueOptionResponse,
  ProfileFields,
  UserProfileResponse,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';

import { Box, Chip, Avatar, SxProps, Theme } from '@mui/material';

import { PublicProfileSection } from './PublicProfileSection';
import { isCurrentlyInEduLevel } from '../utils/options.helpers';

const PUBLIC_PROFILE_STRINGS = appStrings.modules.profile.public;

type SnapshotEnrollmentSectionProps = Readonly<{
  publicProfile?: UserProfileResponse;
  profileValueOptions?: ProfileValueOptionResponse;
  findAllStringValues: (fieldKey: ProfileFields) => { name: string; category: ProfileFields }[];
}>;

export const SnapshotEnrollmentSection = ({
  publicProfile,
  profileValueOptions,
  findAllStringValues,
}: SnapshotEnrollmentSectionProps) => {
  const navigate = useNavigate();

  const collegeName =
    typeof publicProfile?.profile?.academicsPostSecondaryCurrentEnrollment === 'string'
      ? publicProfile?.profile?.academicsPostSecondaryCurrentEnrollment
      : publicProfile?.profile?.academicsPostSecondaryCurrentEnrollment?.school;

  const { data: college } = useGetCollegeByNameQuery(collegeName || '', {
    skip: !collegeName,
  });

  const isInHighSchool = isCurrentlyInEduLevel('high_school', publicProfile, profileValueOptions);

  // if in high school, and there is no current enrollment, we return null
  if (isInHighSchool && !findAllStringValues('academicsHighSchoolCurrentEnrollment').length) {
    return null;
  }

  // if not in high school, and there is no current enrollment, we return null
  if (!isInHighSchool && !findAllStringValues('academicsPostSecondaryCurrentEnrollment').length) {
    return null;
  }

  return (
    <PublicProfileSection
      title={PUBLIC_PROFILE_STRINGS['currentEnrollment']}
      variant={{
        type: 'CHILDREN',
        children: (
          <Box>
            {!isInHighSchool && (
              <Chip
                id={`enrollment-college-chip-${college?.id || collegeName?.toLowerCase().replace(/\s+/g, '-')}`}
                key={college?.id || collegeName}
                label={college?.school_name || collegeName}
                variant="tag"
                size="md"
                sx={chipSx(!!college?.id)}
                onClick={
                  !college?.id
                    ? undefined
                    : () => {
                        navigate(`${APP_ROUTE.UNIVERSITY}/${college?.id}`);
                      }
                }
                avatar={
                  college ? (
                    <Avatar
                      src={college?.logo_media_url || '/images/university-logo-default.svg'}
                    />
                  ) : undefined
                }
              />
            )}

            {isInHighSchool &&
              findAllStringValues('academicsHighSchoolCurrentEnrollment').map((opt) => (
                <Chip key={opt.name} label={opt.name} variant="tag" size="md" sx={chipSx(false)} />
              ))}
          </Box>
        ),
      }}
    />
  );
};

const chipSx =
  (clickable: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    backgroundColor: backrs.colors['Colors/Base/White'],
    cursor: clickable ? 'pointer' : 'default',
    '&:hover': {
      opacity: 0.8,
    },
    width: 'fit-content',
    mb: backrs.spacing['spacing-xs'],
    '& .MuiChip-avatar': {
      marginLeft: `${backrs.spacing['spacing-none']} !important`,
      marginRight: `${backrs.spacing['spacing-xxs']} !important`,
      border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
      width: ICON_SIZES['16px'],
      height: ICON_SIZES['16px'],
      borderRadius: backrs.radius['radius-full'],
    },
  });
